import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import {
  buildTimeEntryStartTime,
  IsoDate,
  IsoDateTime,
  ProjectSummary,
  TimeEntry,
  WorkPositionSummary,
} from '@jessie/commons';
import { DatePipe, KeyValue } from '@angular/common';
import { FormatWorkPipe } from '../../../../pipes/format-work.pipe';
import { FormatSecondsPipe } from '../../../../pipes/format-seconds.pipe';
import { SecondsPassedPipe } from '../../../../pipes/seconds-passed.pipe';
import { NgIconWrapperComponent } from '../../../../components/icons/ng-icon-wrapper/ng-icon-wrapper.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { WorkForm } from '../../../projects/slide-overs/work-slide-over.component';
import { Observable } from 'rxjs';
import { DropdownComponent } from '../../../../components/dropdown/dropdown.component';
import { TextInputComponent } from '../../../../components/input/text-input/text-input.component';
import { WorkTimeInputComponent } from '../../../../components/input/work-time-input/work-time-input.component';
import { TailwindButtonDirective } from '../../../../components/button/tailwind-button.directive';
import { DropdownButtonComponent } from '../../../../components/dropdown-minimal-menu/dropdown-button/dropdown-button.component';
import { DropdownItemComponent } from '../../../../components/dropdown-minimal-menu/dropdown-item/dropdown-item.component';
import { DropdownMinimalMenuComponent } from '../../../../components/dropdown-minimal-menu/dropdown-minimal-menu.component';
import { FormInputWrapperComponent } from '../../../../components/input/form-input-wrapper/form-input-wrapper.component';
import { TailwindInputDirective2 } from '../../../../components/input/tailwind-input2.directive';
import { format, parseISO } from 'date-fns';
export interface TimerData {
  state: 'inactive' | 'active';
  workItemState: 'embedded' | 'linked';
  startTime: IsoDateTime | null;
  date: IsoDate;
  timeEntries: {
    id: string;
    startTime: IsoDateTime;
    endTime: IsoDateTime;
    totalMinutes: number;
    isSameDay: boolean;
  }[] | null;
  isToday: boolean;
}

@Component({
  selector: 'app-timer',
  imports: [
    DatePipe,
    FormatWorkPipe,
    FormatSecondsPipe,
    SecondsPassedPipe,
    NgIconWrapperComponent,
    DropdownComponent,
    TextInputComponent,
    WorkTimeInputComponent,
    TailwindButtonDirective,
    ReactiveFormsModule,
    DropdownButtonComponent,
    DropdownItemComponent,
    DropdownMinimalMenuComponent,
    FormInputWrapperComponent,
    TailwindInputDirective2,
  ],
  templateUrl: './timer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    @reference "tailwindcss";
    :host {
      @apply fixed sm:contents inset-0 mt-16 sm:mt-0 z-10 sm:z-0 overflow-scroll;
      background-color: var(--color-surface-secondary);
    }
  `,
})
export class TimerComponent {
  @Input({ required: true }) timer!: TimerData;
  @Input({ required: true }) workForm!: FormGroup<WorkForm>;
  @Input({ required: true }) positionSummaries!: Observable<WorkPositionSummary[]>;
  @Input({ required: true }) projectSummaries!: Observable<ProjectSummary[]>;
  @Input() editingTimeEntryId: string | '__running' | null = null;
  @Input() timeEntryError: string | null = null;
  @Input() savingTimeEntryId: string | null = null;

  @Output() start = new EventEmitter<void>();
  @Output() stop = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();
  @Output() saveWork = new EventEmitter<void>();
  @Output() navigateBack = new EventEmitter<void>();
  @Output() beginTimeEntryEdit = new EventEmitter<string | '__running'>();
  @Output() cancelTimeEntryEdit = new EventEmitter<void>();
  @Output() updateTimeEntry = new EventEmitter<{ sessionEntryId: string; nextEntry: TimeEntry }>();
  @Output() updateActiveStartTime = new EventEmitter<IsoDateTime>();

  readonly timeEntryStartControl = new FormControl('', { nonNullable: true });
  readonly timeEntryEndControl = new FormControl('', { nonNullable: true });

  compareTimeEntries(a: KeyValue<string, TimeEntry>, b: KeyValue<string, TimeEntry>): number {
    return a.value.startTime.localeCompare(b.value.startTime);
  }

  beginPausedTimeEntryEdit(timeEntry: NonNullable<TimerData['timeEntries']>[number]) {
    if (this.editingTimeEntryId || this.savingTimeEntryId) {
      return;
    }

    this.timeEntryStartControl.setValue(this.toTime(timeEntry.startTime));
    this.timeEntryEndControl.setValue(this.toTime(timeEntry.endTime));
    this.beginTimeEntryEdit.emit(timeEntry.id);
  }

  beginActiveStartTimeEdit() {
    if (this.editingTimeEntryId || this.savingTimeEntryId) {
      return;
    }

    this.timeEntryStartControl.setValue(this.timer.startTime ? this.toTime(this.timer.startTime) : '');
    this.beginTimeEntryEdit.emit('__running');
  }

  savePausedTimeEntry(timeEntry: NonNullable<TimerData['timeEntries']>[number]) {
    const startMinutes = this.toMinutes(this.timeEntryStartControl.value);
    const endMinutes = this.toMinutes(this.timeEntryEndControl.value);
    const startTime =
      startMinutes === null
        ? ''
        : buildTimeEntryStartTime(this.toDate(timeEntry.startTime), startMinutes);

    this.updateTimeEntry.emit({
      sessionEntryId: timeEntry.id,
      nextEntry: {
        startTime,
        totalMinutes: startMinutes === null || endMinutes === null ? Number.NaN : endMinutes - startMinutes,
      },
    });
  }

  saveActiveStartTime() {
    const startMinutes = this.toMinutes(this.timeEntryStartControl.value);
    this.updateActiveStartTime.emit(
      startMinutes === null ? '' : buildTimeEntryStartTime(this.timer.date, startMinutes)
    );
  }

  private toTime(value: IsoDateTime): string {
    const date = parseISO(value);
    return Number.isNaN(date.getTime()) ? '' : format(date, 'HH:mm');
  }

  private toDate(value: IsoDateTime): IsoDate {
    const date = parseISO(value);
    return Number.isNaN(date.getTime()) ? this.timer.date : format(date, 'yyyy-MM-dd');
  }

  private toMinutes(value: string): number | null {
    if (!/^\d{2}:\d{2}$/.test(value)) {
      return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    return hours < 24 && minutes < 60 ? hours * 60 + minutes : null;
  }
}
